/**
 * Mapping of answer text to taste patterns for questions Q8-Q15
 * Each question maps possible answer selections to their corresponding taste pattern
 */
export const answerToPatternMap = {
  q8: {
    "ブラック": "苦",
    "それ以外（コーヒーを飲まない場合もこちら）": "－",
  },
  q9: {
    "パイナップルジュース": "酸甘",
    "グレープフルーツジュース": "苦酸",
  },
  q10: {
    "塩": "塩",
    "タレ": "旨",
  },
  q11: {
    "出汁巻き": "旨塩",
    "砂糖入り": "甘",
  },
  q12: {
    "春菊のお浸し": "苦塩",
    "かぼちゃの煮物": "旨",
  },
  q13: {
    "海苔の佃煮": "旨",
    "ふきの煮物": "苦",
    "フライドポテト": "塩",
    "大学いも": "甘",
    "きゅうりの酢の物": "酸",
  },
  q14: {
    "モンブラン": "旨",
    "フルーツタルト": "酸甘",
    "チョコレートケーキ": "苦",
    "チーズケーキ": "酸",
    "ショートケーキ": "甘",
  },
  q15: {
    "青じそドレッシング": "苦塩",
    "中華ドレッシング": "旨",
    "玉ねぎドレッシング": "甘",
    "シーザードレッシング": "酸",
    "和風醤油ドレッシング": "塩",
  },
};

/**
 * Build taste pattern string from Q8-Q15 answers
 * 
 * @param {Object} questionnaire - The full questionnaire object from the API
 * @param {Object} answers - The answers object containing Q8-Q15 selections
 * @returns {string} Pattern string like "苦_苦酸_旨_旨塩_旨_塩_旨_旨"
 * 
 * @example
 * const pattern = buildTastePattern(questionnaire, answers);
 * // Returns: "苦_苦酸_旨_旨塩_旨_塩_旨_旨"
 */
export const buildTastePattern = (questionnaire, answers) => {
  const patterns = [];
  const questionKeys = ["q8", "q9", "q10", "q11", "q12", "q13", "q14", "q15"];

  for (const qKey of questionKeys) {
    const answerKey = answers[qKey];
    
    if (!answerKey) {
      console.warn(`Missing answer for ${qKey}`);
      patterns.push("－"); // default fallback
      continue;
    }

    // Get the option text from the questionnaire
    const questionData = questionnaire?.questions?.profile_questions?.[qKey];
    if (!questionData) {
      console.warn(`Question ${qKey} not found in questionnaire`);
      patterns.push("－");
      continue;
    }

    const answerText = questionData.options?.[answerKey];
    if (!answerText) {
      console.warn(
        `Answer option "${answerKey}" not found for question ${qKey}`
      );
      patterns.push("－");
      continue;
    }

    // Map the answer text to its taste pattern
    const tastePattern = answerToPatternMap[qKey]?.[answerText];
    if (!tastePattern) {
      console.warn(
        `No taste pattern found for ${qKey}: "${answerText}"`
      );
      patterns.push("－");
      continue;
    }

    patterns.push(tastePattern);
  }

  return patterns.join("_");
};

/**
 * Get taste pattern for a single answer
 * Useful for debugging or checking individual question patterns
 * 
 * @param {string} questionKey - The question key (e.g., "q8", "q9")
 * @param {string} answerText - The answer text/description
 * @returns {string|null} The corresponding taste pattern or null if not found
 */
export const getSinglePatternFromAnswer = (questionKey, answerText) => {
  return answerToPatternMap[questionKey]?.[answerText] || null;
};

/**
 * Reverse lookup: get answer text from taste pattern
 * Useful for displaying what pattern corresponds to
 * 
 * @param {string} questionKey - The question key (e.g., "q8", "q9")
 * @param {string} pattern - The taste pattern (e.g., "苦", "旨塩")
 * @returns {string|null} The corresponding answer text or null if not found
 */
export const getAnswerFromPattern = (questionKey, pattern) => {
  const questionMap = answerToPatternMap[questionKey];
  if (!questionMap) return null;
  
  for (const [answer, pat] of Object.entries(questionMap)) {
    if (pat === pattern) {
      return answer;
    }
  }
  return null;
};
